import { Navigate, Outlet } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { PageViewTracker } from './components/analytics/PageViewTracker'
import { EntrepriseProvider } from './contexts/EntrepriseContext'
import { TooltipProvider } from './components/ui/tooltip'
import { Toaster } from './components/ui/sonner'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { DashboardLayout, AdminLayout } from './components/layout'
import {
  LandingPage,
  DashboardPage,
  SituationsTravailPage,
  QuestionnaireQ1Page,
  QuestionnaireQ2Page,
  SynthesePage,
  ResultsPage,
  Synthese2Page,
  CasquesPage,
  ChoixThemesPage,
  CatalogueSolutionsPage,
  PlanActionsPage,
  AttestationPage,
  SyntheseFinalePage,
  DiagnosticFinPage,
  PlanActionDashboardPage,
  PlanActionDetailPage,
  AttestationDashboardPage,
  SelectionMetiersPage,
  AdminAttestationsPage,
  AdminAttestationDetailPage,
  MentionsLegalesPage,
  ConfidentialitePage,
  ContactPage,
  ProfilePage,
} from './pages'
import type { RouteObject } from 'react-router-dom'

// Root layout — providers + global UI
function RootLayout() {
  return (
    <AuthProvider>
      <EntrepriseProvider>
        <TooltipProvider>
          <PageViewTracker />
          <div className="min-h-screen bg-gray-50">
            <Outlet />
          </div>
          <Toaster position="bottom-right" richColors closeButton />
        </TooltipProvider>
      </EntrepriseProvider>
    </AuthProvider>
  )
}

// Page 404
function NotFound() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Page non trouvée
        </h2>
        <p className="text-gray-600 mb-4">
          La page que vous recherchez n'existe pas.
        </p>
        <a
          href="/"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retour à l'accueil
        </a>
      </div>
    </div>
  )
}

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      // Pages publiques
      { path: '/', element: <LandingPage /> },
      { path: '/mentions-legales', element: <MentionsLegalesPage /> },
      { path: '/confidentialite', element: <ConfidentialitePage /> },
      { path: '/contact', element: <ContactPage /> },
      // Routes protégées — Dashboard (layout route avec sidebar + notifications)
      {
        path: '/dashboard',
        element: <ProtectedRoute><DashboardLayout /></ProtectedRoute>,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'plan-action', element: <PlanActionDashboardPage /> },
          { path: 'plan-action/:id', element: <PlanActionDetailPage /> },
          { path: 'attestation', element: <AttestationDashboardPage /> },
          { path: 'profile', element: <ProfilePage /> },
        ],
      },

      // Redirect legacy
      { path: '/identification', element: <Navigate to="/dashboard" replace /> },

      // Routes protégées par SSO — Tunnel diagnostic
      { path: '/diagnostic/nouveau', element: <ProtectedRoute><SelectionMetiersPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/situations', element: <ProtectedRoute><SituationsTravailPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/q1', element: <ProtectedRoute><QuestionnaireQ1Page /></ProtectedRoute> },
      { path: '/diagnostic/:id/q2', element: <ProtectedRoute><QuestionnaireQ2Page /></ProtectedRoute> },
      { path: '/diagnostic/:id/synthese', element: <ProtectedRoute><SynthesePage /></ProtectedRoute> },
      { path: '/diagnostic/:id/resultats', element: <ProtectedRoute><ResultsPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/synthese-2', element: <ProtectedRoute><Synthese2Page /></ProtectedRoute> },
      { path: '/diagnostic/:id/casques', element: <ProtectedRoute><CasquesPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/themes', element: <ProtectedRoute><ChoixThemesPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/solutions', element: <ProtectedRoute><CatalogueSolutionsPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/finalisation', element: <ProtectedRoute><DiagnosticFinPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/actions', element: <ProtectedRoute><PlanActionsPage /></ProtectedRoute> },
      { path: '/diagnostic/:id/synthese-finale', element: <ProtectedRoute><SyntheseFinalePage /></ProtectedRoute> },
      { path: '/diagnostic/:id/attestation', element: <ProtectedRoute><AttestationPage /></ProtectedRoute> },

      // Routes admin — DashboardLayout + Refine
      {
        path: '/admin',
        element: <ProtectedRoute><DashboardLayout /></ProtectedRoute>,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { path: 'attestations', element: <AdminAttestationsPage /> },
              { path: 'attestations/:id', element: <AdminAttestationDetailPage /> },
            ],
          },
        ],
      },

      // 404
      { path: '*', element: <NotFound /> },
    ],
  },
]
